// src/app/services/user.ts
import { InviteClientForm } from 'admin/features/clients/viewClients/AddNewClientButton';
import { ITimezone } from './constants';
import api, {
  buildQueryParams,
  IGetQueryParams,
  IPaginatedRepsonse,
} from './index';
import {
  AssignBusinessTeamClient,
  AssignIndividualTeamClient,
  BusinessProjectGetAll,
  BusinessProjectGetId,
  IClientBusiness,
} from './responseTypes';

// Enums and interfaces for roles and user data
export enum UserRoleEnum {
  ADMIN = 'ADMIN',
  ACCOUNTANT = 'ACCOUNTANT',
  CLIENT = 'CLIENT',
}

export enum TaxFillingStatus {
  SINGLE = 'Single',
  MARRIED_FILLING_JOINTLY = 'Married Filing Jointly',
  MARRIED_FILLING_SEPARATELY = 'Married Filing Separately',
  HEAD_OF_HOUSEHOLD = 'Head of Household',
  QUALIFYING_SURVIVING_SPOUSE = 'Qualifying Surviving Spouse',
}

export interface IUserRole {
  id: number;
  name: UserRoleEnum;
  created_at: string;
  updated_at: string;
}

export interface ISpouse {
  id: number;
  firstname: string;
  lastname: string;
  ssn: string;
  email: string;
  phone_number: string;
  occupation: string;
  created_at: string;
  updated_at: string;
  user_id: number;
  birth_date: string;
}

export interface IDependent {
  id: number;
  firstname: string;
  lastname: string;
  relationship: string;
  ssn: string;
  birth_date: string;
  created_at: string;
  updated_at: string;
  user_id: number;
}

// For users, our backend returns a minimal department (just an ID)
// so we define a minimal interface for API purposes.
export interface IAPIDepartment {
  id: number;
}

// Then, in the user type we include an optional accountant_profile field:
export interface IAccountantProfile {
  id: number;
  departments?: IAPIDepartment[];
  schedule_platform_link?: string; // ← add this line
}

export interface IUser {
  id: number;
  email: string;
  title?: string;
  family_name: string;
  given_name: string;
  roles?: IUserRole[];
  service_type?: string;
  picture_url?: string;
  sender_number?: string;
  profile_id?: number;
  profile_type?: string;
  phone_number?: string;
  specialization?: { id: number; name: string };
  time_zone?: ITimezone;
  ssn?: string;
  birth_date?: string;
  occupation?: string;
  tax_filing_status?: string;
  spouse?: ISpouse;
  dependents?: IDependent[];
  team_members?: Omit<IUser, 'team_members' | 'dependents' | 'spouse'>[];
  language?: {
    id: number;
    name: string;
  };
  created_at?: string;
  updated_at?: string;
  locale?: string;
  time_zone_id?: number;
  username?: string;
  hear_about_us?: string;
  code?: number;
  zipcode?: string;
  country?: string;
  state?: string;
  city?: string;
  address?: string;
  active_member?: boolean;
  last_activity_at?: string;
  quickbooks?: {
    quickbooks_token_expires_at?: string;
  };
  accountant_profile?: IAccountantProfile;
}

export interface IUpdateUser extends Partial<IUser> {
  dependents_attributes?: any;
  specialization_id?: number | string;
  spouse_attributes?: any;
}

export interface IUserStats {
  active_clients_count: number;
  inactive_clients_count: number;
  clients_total_count: number;
  active_accountants_count: number;
  inactive_accountants_count: number;
  accountants_total_count: number;
}

function updateUserInStore({ userId }: any, { dispatch, queryFulfilled }: any) {
  return queryFulfilled.then((response: any) => {
    dispatch(
      userApi.util.updateQueryData(
        userId ? 'getUserProfileById' : 'getMyProfile',
        userId,
        (draft) => Object.assign(draft, response.data),
      ),
    );
  });
}

export const userApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // … (other endpoints remain unchanged)
    getUsers: builder.query<
      IUser[],
      {
        roles: UserRoleEnum[];
        givenNameSearch?: string;
        accountantId?: number | false;
      }
    >({
      query: ({ roles, givenNameSearch, accountantId }) => {
        let rolesFilter = roles.map((r) => `filters[role][]=${r}`).join('&');
        let nameFilter = givenNameSearch
          ? `&filters[given_name_search]=${givenNameSearch}`
          : '';
        let accountantFilter = accountantId
          ? `&filters[accountant]=${accountantId}`
          : '';
        return `/users?${rolesFilter}${nameFilter}${accountantFilter}`;
      },
      providesTags: () => [{ type: 'Users', id: 'LIST' }],
      transformResponse: (res: { users: IUser[] }) => res.users,
    }),
    getPaginatedUsers: builder.query<
      IPaginatedRepsonse<IUser>,
      IGetQueryParams<{
        search?: string;
        accountant?: number;
        service_type?: string;
        tax_return_requested?: boolean;
        ordered_service?: number;
        active_member?: boolean;
        role: UserRoleEnum[];
      }>
    >({
      query: (params) => ({
        url: `/users/paginated_index${buildQueryParams(params)}`,
      }),
    }),
    getMinimalClients: builder.query<IUser[], void>({
      query: () => `/users/minimal_clients`,
      transformResponse: (res: { clients: IUser[] }) => res.clients,
    }),

    createUser: builder.mutation({
      query: (user: { id_token: string }) => ({
        url: `/users`,
        method: 'POST',
        body: { user: user },
      }),
      invalidatesTags: [{ type: 'User', id: 'Profile' }],
    }),

    getUserProfileById: builder.query<{ user: IUser }, number>({
      query: (id) => `/users/${id}`,
      providesTags: (p) => [{ type: 'User', id: p?.user.id }],
    }),

    getMyProfile: builder.query<{ user: IUser }, void>({
      query: () => '/users/profile',
      providesTags: [{ type: 'User', id: 'Profile' }],
    }),

    updateMyProfile: builder.mutation<{ user: IUser }, { user: IUpdateUser }>({
      query: ({ user }) => ({
        url: `/users/profile`,
        method: 'PUT',
        body: { user },
      }),
      invalidatesTags: () => [{ type: 'Accountant', id: 'Profile' }],
      onQueryStarted: updateUserInStore,
    }),

    updateUserProfile: builder.mutation<
      { user: IUser },
      { user: IUpdateUser; userId: number }
    >({
      query: ({ user, userId }) => ({
        url: `/users/${userId}/profile`,
        method: 'PUT',
        body: { user },
      }),
      onQueryStarted: updateUserInStore,
    }),

    updateMyProfilePicture: builder.mutation<
      { user: IUser },
      { user: FormData }
    >({
      query: ({ user }) => ({
        url: `/users/profile`,
        method: 'PUT',
        body: user,
      }),
      invalidatesTags: () => [{ type: 'Accountant', id: 'Profile' }],
      onQueryStarted: updateUserInStore,
    }),

    updateUserProfilePicture: builder.mutation<
      IUser,
      { user: FormData; userId: number }
    >({
      query: ({ user, userId }) => ({
        url: `/users/${userId}/profile`,
        method: 'PUT',
        body: user,
      }),
      onQueryStarted: updateUserInStore,
    }),

    deleteDependent: builder.mutation({
      query: (id: number) => ({
        url: `/dependents/${id}`,
        method: 'DELETE',
      }),
    }),

    deleteAllDependent: builder.mutation<void, void>({
      query: () => ({
        url: `/dependents`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'User', id: 'Profile' }],
    }),

    getUserBusinesses: builder.query<IClientBusiness[], void>({
      query: () => '/businesses',
      providesTags: () => [
        { type: 'Businesses', id: 'LIST' },
        { type: 'Business' },
      ],
      transformResponse: (response: BusinessProjectGetAll) =>
        response.businesses,
    }),

    getUserBusinessById: builder.query<
      { business: BusinessProjectGetId },
      string | number
    >({
      query: (id) => `/businesses/${id}`,
      providesTags: () => [
        { type: 'Businesses', id: 'LIST' },
        { type: 'Business' },
      ],
    }),

    getAdminUserBusinessById: builder.query<IClientBusiness[], number>({
      query: (id: number) => `/businesses/?client_id=${id}`,
      transformResponse: (res: BusinessProjectGetAll) =>
        res.businesses.map((b) => ({
          ...b,
          business_id: b.id,
          business_name: b.name,
        })),
      providesTags: () => [
        { type: 'Businesses', id: 'LIST' },
        { type: 'Business' },
      ],
    }),

    getAdminBusinesses: builder.query<IClientBusiness[], void>({
      query: () => `/businesses`,
      transformResponse: (res: BusinessProjectGetAll) =>
        res.businesses.map((b) => ({
          ...b,
          business_id: b.id,
          business_name: b.name,
        })),
      providesTags: () => [
        { type: 'Businesses', id: 'LIST' },
        { type: 'Business' },
      ],
    }),

    deleteBusinessTeamClientById: builder.mutation({
      query: ([business_id, assignee_id]: [number, number]) => ({
        url: `/business_teams/unassign?business_id=${business_id}&assignee_id=${assignee_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'Businesses', id: 'LIST' },
        { type: 'Business' },
      ],
    }),

    assignBusinessTeamClient: builder.mutation({
      query: ({
        business_team,
      }: {
        business_team: AssignBusinessTeamClient;
      }) => ({
        url: `/business_teams/assign`,
        method: 'PUT',
        body: { business_team },
      }),
      invalidatesTags: [
        { type: 'Businesses', id: 'LIST' },
        { type: 'Business' },
      ],
    }),

    deleteIndividualTeamClientById: builder.mutation<any, [number, number]>({
      query: ([client_id, assignee_id]) => ({
        url: `/teams/unassign?client_id=${client_id}&assignee_id=${assignee_id}`,
        method: 'DELETE',
      }),
      onQueryStarted: (
        [client_id, assignee_id],
        { queryFulfilled, dispatch },
      ) =>
        queryFulfilled.then(() => {
          dispatch(
            userApi.util.updateQueryData(
              'getUserProfileById',
              client_id,
              (draft) =>
                Object.assign(draft, {
                  user: {
                    ...draft.user,
                    team_members: (draft.user.team_members || []).filter(
                      (tm) => tm.id !== assignee_id,
                    ),
                  },
                }),
            ),
          );
        }),
    }),

    assignIndividualTeamClient: builder.mutation<
      { team_members: IUser[] },
      { team: AssignIndividualTeamClient }
    >({
      query: ({ team }) => ({
        url: `/teams/assign`,
        method: 'PUT',
        body: { team },
      }),
      onQueryStarted: (p, { queryFulfilled, dispatch }) =>
        queryFulfilled.then((response) => {
          dispatch(
            userApi.util.updateQueryData(
              'getUserProfileById',
              p.team.client_id,
              (draft) =>
                Object.assign(draft, {
                  user: {
                    ...draft.user,
                    team_members: response.data.team_members,
                  },
                }),
            ),
          );
        }),
    }),

    createBusinessProject: builder.mutation({
      query: (business: BusinessProjectGetId) => ({
        url: `/businesses`,
        method: 'POST',
        body: business,
      }),
      invalidatesTags: [
        { type: 'Businesses', id: 'LIST' },
        { type: 'Business' },
      ],
    }),

    createClientBusiness: builder.mutation({
      query: (params: {
        clientId: number;
        business: BusinessProjectGetId;
      }) => ({
        url: `/users/${params.clientId}/businesses`,
        method: 'POST',
        body: params.business,
      }),
      invalidatesTags: [
        { type: 'Businesses', id: 'LIST' },
        { type: 'Business' },
      ],
    }),

    updateBusinessProjectLogo: builder.mutation({
      query: ({ id, business }: { id: any; business: FormData }) => ({
        url: `/businesses/${id}`,
        method: 'PUT',
        body: business,
      }),
      invalidatesTags: [
        { type: 'Businesses', id: 'LIST' },
        { type: 'Business' },
      ],
    }),

    updateBusinessProject: builder.mutation({
      query: ({ id, business }: { id: any; business: any }) => ({
        url: `/businesses/${id}`,
        method: 'PUT',
        body: business,
      }),
      invalidatesTags: [
        { type: 'Businesses', id: 'LIST' },
        { type: 'Business' },
      ],
    }),

    getAccountantSignupRequests: builder.query<
      IPaginatedRepsonse<IUser>,
      IGetQueryParams<{ email_search: string; state?: 'pending' | 'rejected' }>
    >({
      query: (params) =>
        `accountant_signup_requests${buildQueryParams(params)}`,
    }),

    acceptAccountantSignupRequest: builder.mutation<IUser, number>({
      query: (id) => ({
        url: `/accountant_signup_requests/${id}/accept`,
        method: 'PUT',
      }),
    }),

    rejectAccountantSignupRequest: builder.mutation<IUser, number>({
      query: (id) => ({
        url: `/accountant_signup_requests/${id}/reject`,
        method: 'PUT',
      }),
    }),

    exportClients: builder.mutation<
      File,
      { from_date: string; to_date: string }
    >({
      query: (params) => ({
        url: `/exports/clients?from_date=${params.from_date}&to_date=${params.to_date}`,
        method: 'Get',
        responseHandler: async (response: Response) =>
          window.location.assign(
            window.URL.createObjectURL(await response.blob()),
          ),
        cache: 'no-cache',
      }),
    }),

    markClientAsActive: builder.mutation<{ user: IUser }, number>({
      query: (userId) => ({
        url: `/users/${userId}/active_member`,
        method: 'Put',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const response = await queryFulfilled;
        dispatch(
          userApi.util.updateQueryData('getUserProfileById', id, (draft) =>
            Object.assign(draft, response.data),
          ),
        );
      },
    }),

    markClientAsInActive: builder.mutation<{ user: IUser }, number>({
      query: (userId) => ({
        url: `/users/${userId}/inactive_member`,
        method: 'Put',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const response = await queryFulfilled;
        dispatch(
          userApi.util.updateQueryData('getUserProfileById', id, (draft) =>
            Object.assign(draft, response.data),
          ),
        );
      },
    }),

    updateClientActiveStatus: builder.mutation<
      { user: IUser },
      { id: number; activate: boolean }
    >({
      query: (params) => ({
        url: `/users/${params.id}/${
          params.activate ? 'active_member' : 'inactive_member'
        }`,
        method: 'Put',
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const response = await queryFulfilled;
        dispatch(
          userApi.util.updateQueryData('getUserProfileById', id, (draft) =>
            Object.assign(draft, response.data),
          ),
        );
      },
    }),

    getCognitoUserAccountStatus: builder.query<
      {
        enabled: boolean;
        user_create_date: string;
        user_last_modified_date: string;
        user_status: 'CONFIRMED' | 'FORCE_CHANGE_PASSWORD';
      },
      number
    >({
      query: (userId) => ({
        url: `/users/${userId}/status`,
      }),
    }),

    resendInvitation: builder.mutation<void, number>({
      query: (userId) => ({
        url: `/users/${userId}/re_invite`,
        method: 'POST',
      }),
    }),

    inviteCLient: builder.mutation<void, InviteClientForm>({
      query: (body) => ({
        url: `/users/client/invite`,
        method: 'POST',
        body: { user: body },
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),

    getUserStats: builder.query<IUserStats, void>({
      query: () => '/users/stats',
    }),

    // ===================== New Department Endpoints =====================
    assignDepartment: builder.mutation<
      any,
      { accountantId: number; departmentId: number }
    >({
      query: ({ accountantId, departmentId }) => ({
        url: `accountant_profiles/${accountantId}/assign_department`,
        method: 'POST',
        body: { department_id: departmentId },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'Profile' }],
    }),
    unassignDepartment: builder.mutation<
      any,
      { accountantId: number; departmentId: number }
    >({
      query: ({ accountantId, departmentId }) => ({
        url: `accountant_profiles/${accountantId}/unassign_department`,
        method: 'DELETE',
        body: { department_id: departmentId },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'Profile' }],
    }),
    getDepartments: builder.query<{ departments: IAPIDepartment[] }, void>({
      query: () => 'departments',
    }),
    // =====================================================================
  }),
});

export const {
  useCreateUserMutation,
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetUserProfileByIdQuery,
  useLazyGetUserProfileByIdQuery,
  useUpdateUserProfileMutation,
  useUpdateUserProfilePictureMutation,
  useDeleteDependentMutation,
  useDeleteAllDependentMutation,
  useGetUserBusinessesQuery,
  useDeleteBusinessTeamClientByIdMutation,
  useAssignBusinessTeamClientMutation,
  useDeleteIndividualTeamClientByIdMutation,
  useAssignIndividualTeamClientMutation,
  useGetAdminUserBusinessByIdQuery,
  useGetUserBusinessByIdQuery,
  useLazyGetUserBusinessByIdQuery,
  useCreateBusinessProjectMutation,
  useCreateClientBusinessMutation,
  useUpdateBusinessProjectMutation,
  useUpdateBusinessProjectLogoMutation,
  useLazyGetAdminUserBusinessByIdQuery,
  useLazyGetAdminBusinessesQuery,
  useLazyGetAccountantSignupRequestsQuery,
  useRejectAccountantSignupRequestMutation,
  useAcceptAccountantSignupRequestMutation,
  useLazyGetPaginatedUsersQuery,
  useLazyGetMinimalClientsQuery,
  useInviteCLientMutation,
  useExportClientsMutation,
  useUpdateClientActiveStatusMutation,
  useGetCognitoUserAccountStatusQuery,
  useResendInvitationMutation,
  useUpdateMyProfilePictureMutation,
  useMarkClientAsActiveMutation,
  useMarkClientAsInActiveMutation,
  useGetMyProfileQuery,
  useLazyGetMyProfileQuery,
  useUpdateMyProfileMutation,
  useGetUserStatsQuery,
  // New Department Hooks
  useAssignDepartmentMutation,
  useUnassignDepartmentMutation,
  useGetDepartmentsQuery,
} = userApi;

export const useGetMyProfileQueryState =
  userApi.endpoints.getMyProfile.useQueryState;
export const useGetUserProfileByIdQueryState =
  userApi.endpoints.getUserProfileById.useQueryState;
